import { useDateSegment } from '@react-aria/datepicker';
import cn from 'classnames';
import { forwardRef, useRef } from 'react';
import { mergeRefs } from 'react-merge-refs';

import type { ForwardedRef, ReactElement } from 'react';

import type { DateSegmentProps } from './date-segment.types';

import './date-segment.scss';

/**
 * The `DateSegment` component is used to render a segment of a date input.
 *
 * @internal
 */
const DateSegment = forwardRef(
  (props: DateSegmentProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
    const {
      children,
      className,
      segment,
      state,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const ref = useRef<HTMLElement>(null);
    const { segmentProps } = useDateSegment(segment, state, ref);

    const mergedRef = mergeRefs([ref, forwardedRef]);

    return (
      <span
        {...segmentProps}
        className={cn(
          'rbui-date-input__segment',
          {
            'rbui-date-input__segment--day-period': segment.type === 'dayPeriod',
            'rbui-date-input__segment--placeholder': segment.isPlaceholder,
            'rbui-date-input__segment--time-zone': segment.type === 'timeZoneName',
          },
          className,
        )}
        ref={mergedRef}
        {...rest}
      >
        {segment.text}
      </span>
    );
  },
);

DateSegment.displayName = 'DateSegment';

export default DateSegment;
